export class ProofError extends Error {
    constructor(code, retryAfter = 0) {
        super(code);
        this.name = "ProofError";
        this.code = code;
        this.retryAfter = retryAfter;
    }
}

function throwIfAborted(signal) {
    if (signal?.aborted) throw new DOMException("The request was aborted", "AbortError");
}

// Cancelling one component must not cancel verification shared by other requests.
function waitFor(promise, signal) {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(new DOMException("The request was aborted", "AbortError"));
    return new Promise((resolve, reject) => {
        const abort = () => reject(new DOMException("The request was aborted", "AbortError"));
        signal.addEventListener("abort", abort, { once: true });
        promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
    });
}

async function errorData(response) {
    try { return await response.clone().json(); } catch { return {}; }
}

/**
 * Build the shared API fetch wrapper for one frontend application.
 * Returns normal Responses, preserves request bodies/signals, and includes the
 * anonymous cookie. Only explicit PoW admission failures trigger bounded retries;
 * quota/storage errors reject with ProofError so the UI can explain the failure.
 */
export function createProofClient({ baseUrl, fetchImpl, solveProof, onStatus = () => {}, storage = null, storageKey = "pow-allowance" }) {
    // A local expiry hint avoids known-expired requests; the server still
    // verifies the real balance and binding on every API call.
    let readyUntil = 0;
    let remaining = 0;
    let allowanceId = null;
    let lowWatermark = 100;
    let renewAfter = 0;
    let renewalPromise = null;

    // These values only avoid round trips. They cannot authorize access: cookies,
    // credit accounting and proof verification remain entirely server-side.
    function persist() {
        try {
            if (allowanceId && Number.isFinite(readyUntil)) {
                storage?.setItem(storageKey, JSON.stringify({ allowanceId, remaining, readyUntil, lowWatermark }));
            } else storage?.removeItem(storageKey);
        } catch { /* Private browsing or full storage must not break API access. */ }
    }
    function restore() {
        try {
            const saved = JSON.parse(storage?.getItem(storageKey) || "null");
            if (!saved || typeof saved.allowanceId !== "string" ||
                !Number.isSafeInteger(saved.remaining) || saved.remaining < 0 ||
                !Number.isFinite(saved.readyUntil) || saved.readyUntil <= Date.now() ||
                saved.readyUntil > Date.now() + 3600000) return;
            if (saved.allowanceId === allowanceId) remaining = Math.min(remaining, saved.remaining);
            else {
                allowanceId = saved.allowanceId;
                remaining = saved.remaining;
                readyUntil = saved.readyUntil;
                lowWatermark = Number.isFinite(saved.lowWatermark) ? Math.max(100, saved.lowWatermark) : 100;
            }
        } catch { /* Invalid hints are ignored. */ }
    }

    function requestCost(url) {
        // Mirrors route decorators only as an estimate. Server headers correct it;
        // changing this value never changes the backend's actual charge.
        return /\/api\/v2\/(?:get_player_report|fixme|refresh)(?:\/|$)/.test(new URL(url, window.location.href).pathname) ? 100 : 1;
    }

    function renewIfLow() {
        if (renewalPromise || readyUntil === Infinity || remaining > lowWatermark ||
            Date.now() >= readyUntil || Date.now() < renewAfter) return;
        // Complete the whole exchange now, while current credits remain usable.
        // No idle timers: subsequent activity can retry after a failed renewal.
        renew(generation, true).catch(() => {});
    }
    function observe(response, requestGeneration, requestAllowance) {
        if (generation !== requestGeneration || allowanceId !== requestAllowance) return;
        const balance = response.headers.get("X-Pow-Remaining");
        const id = response.headers.get("X-Pow-Allowance");
        const ttl = Number(response.headers.get("X-Pow-Expires-In"));
        if (balance !== null && id === allowanceId && Number.isSafeInteger(Number(balance)) && Number(balance) >= 0) {
            // Replies can arrive out of order. Never resurrect spent credits.
            remaining = Math.min(remaining, Number(balance));
            if (ttl > 0) readyUntil = Math.min(readyUntil, Date.now() + ttl * 1000 - 1000);
            persist();
        }
        if (response.ok) renewIfLow();
    }
    // Local renewal version, not the server's allowance generation or balance.
    let generation = 0;
    let blockedUntil = 0;

    // Keep the server's Retry-After deadline across callers. A new proof must not
    // bypass a cooldown; the backend remains authoritative about actual usage.
    function report(error) {
        const retryAt = error.retryAfter ? Date.now() + error.retryAfter * 1000 : 0;
        blockedUntil = Math.max(blockedUntil, retryAt);
        onStatus({ state: "error", code: error.code || "pow_unavailable", retryAt });
    }

    async function controlRequest(path, options = {}) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);
        try {
            const response = await fetchImpl(baseUrl + path, {
                ...options, credentials: "include", cache: "no-store", signal: controller.signal,
            });
            if (!response.ok) {
                const data = await errorData(response);
                const retry = Number(data.retry_after || response.headers.get("Retry-After")) || 0;
                throw new ProofError(data.code || "pow_unavailable", retry);
            }
            return await response.json();
        } finally {
            clearTimeout(timeout);
        }
    }

    function rememberAllowance(data) {
        const ttl = Number(data.expires_in);
        readyUntil = data.disabled ? Infinity : Date.now() +
            (Number.isFinite(ttl) && ttl > 0 ? Math.max(0, ttl * 1000 - 1000) : 0);
        remaining = data.disabled ? Infinity : Math.max(0, Number(data.remaining) || 0);
        allowanceId = data.allowance_id || null;
        lowWatermark = Math.max(100, Math.ceil(remaining * 0.1));
        persist();
    }

    /** Prepare once on page load; data requests join the same in-flight work.
     * Reuse a live allowance on reload, and renew known expiry before requesting
     * data. Idle pages do not repeatedly consume CPU with background timers.
     */
    function prepare(cost = 1) {
        restore();
        if (Date.now() < readyUntil && remaining >= cost) {
            renewIfLow();
            return Promise.resolve();
        }
        // An exhausted caller joins background work and surfaces any failure.
        if (renewalPromise) return renewalPromise.catch(error => { report(error); throw error; });
        return renew(generation);
    }

    async function renew(requestGeneration, background = false) {
        // A delayed 403 may belong to an older allowance already renewed by another
        // caller. Otherwise share one solve/redeem operation across pending calls.
        if (generation !== requestGeneration) return;
        if (!renewalPromise) {
            if (!background) {
                readyUntil = 0;
                allowanceId = null;
                persist();
            }
            renewalPromise = (async () => {
                if (Date.now() < blockedUntil) {
                    throw new ProofError("pow_rate_limited", Math.ceil((blockedUntil - Date.now()) / 1000));
                }
                if (!background) onStatus({ state: "working" });
                // Another tab or lost in-memory state during a restart can make a
                // signed challenge stale (409). Fetch a fresh one, with a retry cap.
                for (let attempt = 0; attempt < 2; attempt += 1) {
                    const challenge = await controlRequest("/pow/challenge");
                    if (challenge.disabled || challenge.ready) {
                        rememberAllowance(challenge);
                        generation += 1;
                        if (!background) onStatus({ state: "idle" });
                        return;
                    }
                    const payload = await solveProof(challenge);
                    if (!payload) throw new ProofError("pow_failed");
                    try {
                        const allowance = await controlRequest("/pow/redeem", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ payload }),
                        });
                        rememberAllowance(allowance);
                        generation += 1;
                        if (!background) onStatus({ state: "idle" });
                        return;
                    } catch (error) {
                        if (error.code !== "pow_stale" || attempt === 1) throw error;
                    }
                }
            })().catch(error => {
                renewAfter = Date.now() + Math.max(30, error.retryAfter || 0) * 1000;
                if (error.retryAfter) blockedUntil = Math.max(blockedUntil, renewAfter);
                if (!background) report(error);
                throw error;
            })
                .finally(() => { renewalPromise = null; });
        }
        return renewalPromise;
    }

    async function apiFetch(input, options = {}) {
        // Clone for each retry so a POST body is reusable. Aborting this caller stops
        // its wait/request, while waitFor leaves other callers' shared proof running.
        const request = new Request(input, { ...options, credentials: "include" });
        const signal = request.signal;
        throwIfAborted(signal);
        const cost = requestCost(request.url);
        await waitFor(prepare(cost), signal);
        // Small adaptive grants may require several waves for a burst of API calls.
        for (let attempt = 0; attempt < 4; attempt += 1) {
            throwIfAborted(signal);
            const requestGeneration = generation;
            const requestAllowance = allowanceId;
            // Reserve immediately so concurrent callers see pending spending.
            remaining = Math.max(0, remaining - cost);
            persist();
            let response;
            try {
                response = await fetchImpl(request.clone());
            } catch (error) {
                if (error.name !== "AbortError") report(new ProofError("pow_unavailable"));
                throw error;
            }
            observe(response, requestGeneration, requestAllowance);
            if (response.ok) return response;
            const data = await errorData(response);
            // Missing state after a backend restart follows the same renewal path as
            // exhausted credits. Replay is safe because this 403 precedes the handler.
            if (response.status === 403 && data.code === "pow_required" && attempt < 3) {
                await waitFor(renew(requestGeneration), signal).catch(error => { report(error); throw error; });
                continue;
            }
            if (data.code?.startsWith("pow_")) {
                const error = new ProofError(data.code, Number(data.retry_after || response.headers.get("Retry-After")) || 0);
                report(error);
                throw error;
            }
            // Never let an error body (including an HTML nginx 429/503) become
            // player/leaderboard data. It would break components expecting data.
            const error = new ProofError(response.status === 429 ? "pow_rate_limited" : "pow_unavailable",
                Number(response.headers.get("Retry-After")) || 0);
            error.status = response.status;
            report(error);
            throw error;
        }
        throw new ProofError("pow_required");
    }
    apiFetch.prepare = () => prepare();
    return apiFetch;
}
